// Revisão das sugestões extraídas do site/Instagram: o usuário escolhe o que aplicar no formulário.
import { useState } from 'react'
import { Check } from 'lucide-react'
import { brl } from '@impulso/shared'
import type { BusinessProfileDto, ProfileExtractResponse } from '@impulso/shared/api/ai'

type Key = 'products' | 'differentials' | 'toneOfVoice' | 'idealCustomer' | 'delivers' | 'serviceRadiusKm' | 'seasonality'

export default function ExtractReview({ result, onApply, onDiscard }: { result: ProfileExtractResponse; onApply: (patch: Partial<BusinessProfileDto>) => void; onDiscard: () => void }) {
  const s = result.suggestion
  const available: Array<{ key: Key; label: string; preview: string }> = []
  if (s.products.length) available.push({ key: 'products', label: 'Produtos e serviços', preview: s.products.map((p) => (p.priceFrom != null ? `${p.name} (a partir de ${brl(p.priceFrom, 2)})` : p.name)).join(' · ') })
  if (s.differentials.length) available.push({ key: 'differentials', label: 'Diferenciais', preview: s.differentials.join(' · ') })
  if (s.toneOfVoice) available.push({ key: 'toneOfVoice', label: 'Tom de voz', preview: s.toneOfVoice })
  if (s.idealCustomer) available.push({ key: 'idealCustomer', label: 'Cliente ideal', preview: s.idealCustomer })
  if (s.delivers != null) available.push({ key: 'delivers', label: 'Faz entrega', preview: s.delivers ? 'Sim' : 'Não' })
  if (s.serviceRadiusKm != null) available.push({ key: 'serviceRadiusKm', label: 'Raio de atendimento', preview: `${s.serviceRadiusKm} km` })
  if (s.seasonality) available.push({ key: 'seasonality', label: 'Datas importantes', preview: s.seasonality })
  const [chosen, setChosen] = useState<Set<Key>>(new Set(available.map((a) => a.key)))

  const toggle = (k: Key) => setChosen((c) => {
    const n = new Set(c)
    if (n.has(k)) n.delete(k)
    else n.add(k)
    return n
  })

  const apply = () => {
    const patch: Partial<BusinessProfileDto> = {}
    for (const k of chosen) (patch as Record<string, unknown>)[k] = s[k]
    onApply(patch)
  }

  return (
    <div className="card stack" style={{ gap: 12, borderColor: 'var(--primary-100)' }}>
      <div>
        <h3>Revise o que encontramos</h3>
        <p className="small muted">Nada foi salvo ainda. Marque o que está certo, aplique no formulário e depois clique em “Salvar perfil”.</p>
      </div>
      {result.warnings.map((w) => <div key={w} className="callout amber small">{w}</div>)}
      {available.length === 0 && <p className="muted">Não encontramos informações úteis nesse endereço. Preencha o perfil abaixo do seu jeito.</p>}
      {available.map((a) => (
        <button key={a.key} type="button" className={`option-card ${chosen.has(a.key) ? 'on' : ''}`} style={{ padding: 12 }} onClick={() => toggle(a.key)} aria-pressed={chosen.has(a.key)}>
          <span style={{ minWidth: 0 }}>
            <span className="small strong" style={{ display: 'block' }}>{a.label}</span>
            <span className="small" style={{ overflowWrap: 'anywhere' }}>{a.preview}</span>
          </span>
          {chosen.has(a.key) ? <Check size={18} color="var(--primary)" style={{ flexShrink: 0 }} /> : <span className="radio" />}
        </button>
      ))}
      <div className="row wrap" style={{ gap: 8 }}>
        {available.length > 0 && <button type="button" className="btn primary" onClick={apply} disabled={chosen.size === 0}>Aplicar no formulário</button>}
        <button type="button" className="btn ghost" onClick={onDiscard}>Descartar</button>
      </div>
    </div>
  )
}
